import {
    EVENT_DESTROY_SIDEGUIDE_BANNER,
    EVENT_INIT_SIDEGUIDE_BANNER,
    EVENT_SIDEGUIDE_AD_RENDERED,
    SIDEGUIDE_BANNER_CONTAINER_ID,
} from 'Utils/types';
import { MIN_SCREEN_HEIGHT } from './blockers';
import { requireElement } from './dom';
import { BrandAlertPopup } from './popup';
import type { PlatformSignals } from './signals';
import type {
    Blockers,
    BrandAlertCreativeData,
    BrandAlertSurface,
    DismissType,
    Host,
    ManualBlock,
    PopupDelegate,
    ShowTrigger,
    TrackPayload,
    Transport,
} from './types';
import { DISMISS_TYPE } from './types';

const POSTPONE_INTERVAL_MS = 1000;
const TRANSITION_SETTLE_MS = 100;
const HIDDEN_CLASS = 'fade-hidden';
const TRANSITION_SLIDE_MS = 1600;

// GAM slots and DOM IDs are shared by all alert clients on the page.
let sideguideRequestId = 0;

export interface BrandAlertsClientDeps {
    surface: BrandAlertSurface;
    popupFrame: HTMLElement;
    transport: Transport;
    host: Host;
    trigger: ShowTrigger;
    blockers: Blockers;
    signals: PlatformSignals;
    /** Present when the surface exposes `setBlocked()` to the page. */
    manualBlocker?: ManualBlock;
}

/**
 * Brand alert (SideGuide) lifecycle.
 *
 * Surface and platform agnostic: knows nothing about Brightcove, the video page
 * DOM, the assistant, or the iOS bridge — all of that arrives through
 * {@link BrandAlertsClientDeps}. Consumers drive it with `start()`,
 * `setBlocked()`, `dismiss()` and `destroy()`, plus two lifecycle events on
 * `document`.
 *
 * @fires brand-alert:shown - CustomEvent on document, `detail: { surface }`.
 *   Fired when the alert transitions to its expanded state.
 * @fires brand-alert:dismissed - CustomEvent on document,
 *   `detail: { surface, type }`. Fired when the alert is dismissed for any
 *   reason — manual, auto, comments-active, page-close.
 */
export class BrandAlertsClient {
    private _activeAlert: BrandAlertPopup | null = null;
    private _nextAlert: BrandAlertPopup | null = null;
    private _pristinePopup: HTMLElement | null = null;
    /**
     * containerId -> fn removing that alert's creative-id listener
     */
    private readonly _pendingCaptures = new Map<string, VoidFunction>();
    /**
     * In-flight guard for the rotation fetch (set synchronously).
     */
    private _rotationPending = false;
    private _postponeTimer: ReturnType<typeof setTimeout> | null = null;
    /**
     * The settle timer, then the slide timer — they run in sequence, so one
     * handle covers both. Cleared on destroy so a rotation caught mid-transition
     * cannot promote an alert into a torn-down client.
     */
    private _transitionTimer: ReturnType<typeof setTimeout> | null = null;
    private _destroyed = false;
    private _started = false;

    constructor(private readonly _deps: BrandAlertsClientDeps) {}

    /**
     * Fetch a creative, build its popup, and show it on the normal expand
     * schedule.
     *
     * @param dieAndReport - event name to track instead of showing, when an
     *   external reason (the chatbot product tour) means the alert must
     *   not display. The creative is still fetched and the event still recorded
     *   against the resulting `BrandAlertViewed`.
     */
    start(dieAndReport: string | false = false): void {
        // A second start() would build another popup over the same DOM, orphaning
        // the first with its listeners live, re-subscribe the signals, and leak
        // the trigger's previous native listener.
        if (this._destroyed || this._started) {
            return;
        }

        this._started = true;
        this._deps.trigger.onReady(() => this._showExpanded());
        // The trigger owns when a creative may be requested: immediately on the
        // video player page, on the first submitted prompt for BVA.
        this._deps.trigger.onArm(() => void this._loadAndShowAlert(dieAndReport));
        this._deps.trigger.start();
    }

    /**
     * Block or unblock the alert from showing.
     *
     * @param reason - raw analytics event name to track if the show is bailed
     *   because of this block (e.g. `'comments_prevent_showing'`). Not a
     *   {@link DISMISS_TYPE} — that enum is for post-show dismissals.
     */
    setBlocked(blocked: boolean, reason: string): void {
        if (this._destroyed) return;
        this._deps.manualBlocker?.set(blocked, reason);
    }

    /**
     * Dismiss any active alert (no-op if none, or if it is still in START or
     * already CLOSED).
     */
    dismiss(reason: DismissType): void {
        // The page holds the reference, so this is the path most likely to be
        // called after teardown. `popup.destroy()` aborts listeners but leaves the
        // state alone, so the guards below would not catch it — and on iOS
        // `host.close()` navigates the webview.
        if (this._destroyed) {
            return;
        }
        const alert = this._activeAlert;
        if (alert && !alert.isPending() && !alert.isClosed()) {
            alert.dismiss(reason);
        }
    }

    /**
     * Remove every listener and timer this client owns.
     */
    destroy(): void {
        this._destroyed = true;

        if (this._postponeTimer) {
            clearTimeout(this._postponeTimer);
        }
        this._postponeTimer = null;

        if (this._transitionTimer) {
            clearTimeout(this._transitionTimer);
        }
        this._transitionTimer = null;

        this._activeAlert?.destroy();
        this._nextAlert?.destroy();

        this._teardownSideguideBanners();
        this._pendingCaptures.forEach((stop) => stop());
        this._pendingCaptures.clear();

        this._deps.host.reset();
        this._deps.trigger.destroy();
        this._deps.blockers.destroy();
        this._deps.signals.destroy();
        this._deps.host.destroy();
    }

    private get _delegate(): PopupDelegate {
        return {
            track: (payload: TrackPayload) => {
                this._deps.transport.track(payload);
            },
            beforeExpand: (popup: BrandAlertPopup) => {
                this._deps.host.expand(popup.container);
            },
            onDismissTimerElapsed: (popup: BrandAlertPopup) => {
                // Only the alert currently on screen may start a rotation.
                if (popup === this._activeAlert) {
                    void this._showNext();
                }
            },
            onDismissed: (popup: BrandAlertPopup, type: DismissType) => {
                this._onDismissed(popup, type);
            },
        };
    }

    // A sideguide-ad creative also kicks off its ad request here; the show doesn't wait on the ad — it
    // fills into the container during the pre-expand window and the creative id is recorded if/when it
    // renders. Called once per open; if the endpoint has no creative to serve the popup stays hidden.
    private async _loadAndShowAlert(dieAndReport: string | false): Promise<void> {
        let data: BrandAlertCreativeData;
        try {
            data = await this._deps.transport.fetchCreative();
        } catch {
            return; // nothing to serve
        }

        if (this._destroyed) {
            return;
        }

        const popup = requireElement<HTMLElement>(this._deps.popupFrame, '.js_brandAlertPopup');

        // Keep a pristine copy of the shell so a rotation can clone a clean popup.
        this._pristinePopup = popup.cloneNode(true) as HTMLElement;

        this._activeAlert = new BrandAlertPopup(popup, data, this._delegate);

        if (dieAndReport) {
            this._deps.trigger.destroy();
            this._activeAlert.trackEvent({ eventName: dieAndReport });
            return;
        }

        this._deps.trigger.scheduleShow(data);
        this._armBannerIfNeeded(this._activeAlert);

        this._deps.host.setPopupHeight(this._activeAlert.root, this._activeAlert.secondaryWrap);
        this._deps.host.reveal();
        this._subscribeSignals();
    }

    private _showExpanded(): void {
        if (this._destroyed) {
            return;
        }

        // A new ready callback supersedes any retry left from the previous idle period.
        if (this._postponeTimer) {
            clearTimeout(this._postponeTimer);
        }
        this._postponeTimer = null;

        const alert = this._activeAlert;
        if (!alert || !alert.isPending() || !this._deps.trigger.isReady()) {
            return;
        }

        const verdict = this._deps.blockers.evaluate();
        if (verdict.kind === 'block') {
            this._deps.trigger.destroy();
            // Guard against a caller that flipped a block without setting a reason.
            // Skip the track instead of firing with `undefined`.
            if (verdict.reason) alert.trackEvent({ eventName: verdict.reason });
            return;
        }
        if (verdict.kind === 'postpone') {
            this._postponeTimer = setTimeout(() => this._showExpanded(), POSTPONE_INTERVAL_MS);
            return;
        }

        this._deps.trigger.destroy();
        document.dispatchEvent(new CustomEvent('brand-alert:shown', { detail: { surface: this._deps.surface } }));
        alert.expand();
    }

    private _onDismissed(popup: BrandAlertPopup, type: DismissType): void {
        // Mid-rotation the incoming popup is the visible one, so it is the one the
        // HCP can close. Either way the display is over: stop the transition and
        // release both popups' banner slots.
        if (this._transitionTimer) clearTimeout(this._transitionTimer);
        this._transitionTimer = null;
        this._teardownSideguideBanners();

        // Whichever popup was *not* closed must stop looking dismissable, or a
        // later page-close or fullscreen signal dismisses it a second time and
        // fires another `brand-alert:dismissed` with a type that never happened.
        const active = this._activeAlert;
        if (active && active !== popup) active.markClosed();

        const shell = active ?? popup;
        this._deps.host.close(shell.root, shell.container);

        active?.destroy();
        popup.destroy();
        this._activeAlert = null;

        document.dispatchEvent(
            new CustomEvent('brand-alert:dismissed', { detail: { surface: this._deps.surface, type } }),
        );
    }

    // --- rotation ---------------------------------------------------------

    private async _showNext(): Promise<void> {
        const active = this._activeAlert;
        // Ignore re-entry while a transition is already in flight. `_nextAlert` covers the fill-wait
        // window, but it is only set after the await below — so `_rotationPending`, set synchronously
        // here, also blocks a second call landing during the fetch round-trip.
        if (!active || active.isClosed() || this._nextAlert || this._rotationPending) return;
        this._rotationPending = true;

        let data: BrandAlertCreativeData;
        try {
            data = await this._deps.transport.fetchNextCreative();
        } catch {
            this._rotationPending = false;
            if (this._destroyed) return;
            active.dismiss(DISMISS_TYPE.AUTO_DISMISS);
            return;
        }

        // Fetch done; `_nextAlert` (set just below) now guards re-entry through the fill wait.
        this._rotationPending = false;
        if (this._destroyed) return;
        // If the alert was dismissed while this fetch was in flight, don't resurrect it.
        if (active.isClosed()) return;
        if (!this._pristinePopup) {
            throw new Error('brand-alerts: rotation started before the pristine shell was captured');
        }

        // Clone the pristine shell (not the live/rendered popup) so the incoming alert starts clean —
        // no stale ad iframe, no duplicate rendered banner. The popup repopulates it from `data`,
        // injecting the ad container only if this creative is a banner.
        const nextPopup = this._pristinePopup.cloneNode(true) as HTMLElement;
        nextPopup.classList.add(HIDDEN_CLASS);
        active.root.after(nextPopup);
        this._nextAlert = new BrandAlertPopup(nextPopup, data, this._delegate);
        this._armBannerIfNeeded(this._nextAlert);

        // Transition straight away whether or not the incoming creative has an ad.
        active.finalizeAlert(DISMISS_TYPE.AUTO_DISMISS);
        this._transitionTimer = setTimeout(() => this._makeTransition(), TRANSITION_SETTLE_MS);
    }

    private _makeTransition(): void {
        if (this._destroyed) return;
        // The incoming alert can be torn down before this fires — a dismiss during the settle window
        // runs `_teardownSideguideBanners`, which nulls `_nextAlert`. Nothing to transition to.
        const active = this._activeAlert;
        if (!this._nextAlert || !active) return;

        active.root.classList.add(HIDDEN_CLASS);
        this._nextAlert.root.classList.remove(HIDDEN_CLASS);

        this._transitionTimer = setTimeout(() => {
            // A dismiss during the slide runs `_teardownSideguideBanners`, nulling `_nextAlert` — abort
            // rather than promote null. Teardown already destroyed the outgoing slot and hid it.
            const incoming = this._nextAlert;
            if (this._destroyed || !incoming) return;

            // The outgoing popup leaves the DOM now — tear down its banner slot if it had one.
            this._destroySideguideBanner(active);
            active.destroy();
            active.root.remove();
            this._activeAlert = incoming;
            this._nextAlert = null;
            // Re-apply per-alert layout the pristine clone doesn't carry: header-row count and the
            // platform popup styles (iOS writes inline top/height when sizing).
            incoming.promote();
            this._deps.host.setPopupHeight(incoming.root, incoming.secondaryWrap);
        }, TRANSITION_SLIDE_MS);
    }

    private _armBannerIfNeeded(alert: BrandAlertPopup): void {
        // A sideguide-ad creative embeds the ad container div (baked into its expanded_html by the admin
        // editor). Detect it by that known constant id — no dependency on __VUMEDI_BANNER_CONTEXT__,
        // which is absent on the iOS brand-alert page. Scope the lookup to this popup: during a
        // transition two popups sit in the DOM at once, so a document-wide lookup could resolve the
        // other. Intentionally does NOT check backend banner config: on a page where the banners module
        // isn't loaded, nothing handles EVENT_INIT_SIDEGUIDE_BANNER, so the ad div just stays empty.
        if (!alert.root.querySelector(`#${SIDEGUIDE_BANNER_CONTAINER_ID}`)) {
            return;
        }

        // Give this alert's ad container a unique id so an outgoing and an incoming banner can be on
        // screen at once without their GAM slots colliding. The creative bakes in the base id; we
        // detect on that then rename to base-N here.
        const containerId = `${SIDEGUIDE_BANNER_CONTAINER_ID}-${(sideguideRequestId += 1)}`;
        requireElement(alert.root, `#${SIDEGUIDE_BANNER_CONTAINER_ID}`).id = containerId;
        alert.sideguideContainerId = containerId;

        this._captureBannerCreative(containerId, alert);

        // Ask the banners module to build the slot; the placement id is applied as the ba_placement_id
        // GAM target (a dedicated key, not the video placement_id one).
        document.dispatchEvent(
            new CustomEvent(EVENT_INIT_SIDEGUIDE_BANNER, {
                detail: { placementId: alert.placementId, containerId },
            }),
        );
    }

    private _captureBannerCreative(containerId: string, alert: BrandAlertPopup): void {
        // The ad requests at page load and usually fills before the popup expands, so the show isn't
        // gated on it. Passively record the GAM creative id when the render for this container arrives.
        // Keyed per container (not a single global slot) so arming the incoming alert's capture doesn't
        // drop the outgoing alert's. Only a NON-empty render resolves it: an empty render may be
        // followed by a fill, so keep listening; teardown/destroy cleans up the rest.
        this._stopCapture(containerId);

        const onRendered = (e: Event) => {
            const detail = (e as CustomEvent<{ containerId?: string; isEmpty?: boolean; creativeId?: string }>).detail;
            if (detail?.containerId !== containerId || detail?.isEmpty) {
                return;
            }
            alert.bannerCreativeId = detail.creativeId ?? null;
            this._stopCapture(containerId);
        };

        document.addEventListener(EVENT_SIDEGUIDE_AD_RENDERED, onRendered);

        this._pendingCaptures.set(containerId, () =>
            document.removeEventListener(EVENT_SIDEGUIDE_AD_RENDERED, onRendered),
        );
    }

    private _stopCapture(containerId: string): void {
        const stop = this._pendingCaptures.get(containerId);

        if (stop) {
            stop();
            this._pendingCaptures.delete(containerId);
        }
    }

    private _destroySideguideBanner(alert: BrandAlertPopup | null): void {
        // A banner slot lives exactly as long as its popup: tell the banners module to destroy it when
        // the popup is discarded (skip, no-fill, transition-out, dismiss).
        if (!alert?.sideguideContainerId) return;
        this._stopCapture(alert.sideguideContainerId);
        document.dispatchEvent(
            new CustomEvent(EVENT_DESTROY_SIDEGUIDE_BANNER, {
                detail: { containerId: alert.sideguideContainerId },
            }),
        );
        alert.sideguideContainerId = null;
    }

    private _teardownSideguideBanners(): void {
        // On close/dismiss, tear down every banner slot this client owns so none leaks a defined GAM
        // slot: drop any half-built incoming alert (which would also wedge the rotation re-entry
        // guard), then destroy the shown alert's slot. Every pending creative-id listener is keyed by a
        // live alert's container id, so routing both alerts through `_destroySideguideBanner` means no
        // listener escapes.
        if (this._nextAlert) {
            this._destroySideguideBanner(this._nextAlert);
            this._nextAlert.destroy();
            this._nextAlert.root.remove();
            this._nextAlert = null;
        }
        // Clear the in-flight guard too, so a close racing a fetch that never resolves can't wedge it.
        this._rotationPending = false;
        this._destroySideguideBanner(this._activeAlert);
    }

    private _subscribeSignals(): void {
        // The player wrapper exposes no `off`, so its fullscreen listener outlives
        // destroy(); this guard is what actually makes every signal inert afterwards.
        const live = (handler: () => void) => () => {
            if (this._destroyed) return;
            handler();
        };

        this._deps.signals.subscribe({
            pageClose: live(() => {
                this._activeAlert?.dismiss(DISMISS_TYPE.PAGE_CLOSE_DISMISS);
            }),
            resize: live(() => {
                this._onResize();
            }),
            focusLost: live(() => {
                const alert = this._activeAlert;
                if (alert) alert.trackEvent({ eventName: 'window_focus_lost', data: alert.getExpandedDurationData() });
            }),
            focusGained: live(() => {
                this._activeAlert?.trackEvent({ eventName: 'window_focus_retrieved' });
            }),
            fullscreenEnter: live(() => {
                const alert = this._activeAlert;
                if (alert?.shown) alert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: 'full screen' });
            }),
            handlebarExpanded: live(() => {
                this._activeAlert?.trackEvent({ eventName: 'handlebar_expanded' });
            }),
            handlebarCollapsed: live(() => {
                this._activeAlert?.trackEvent({ eventName: 'handlebar_collapsed' });
            }),
        });
    }

    private _onResize(): void {
        const alert = this._activeAlert;
        if (!alert) {
            return;
        }

        // recalculate the number of header link rows to apply correct styles for expanded view
        alert.updateHeaderRows();

        if (alert.isPending()) {
            return;
        }

        if (window.innerHeight < MIN_SCREEN_HEIGHT) {
            alert.dismiss(DISMISS_TYPE.AUTO_DISMISS, { reason: `height < ${MIN_SCREEN_HEIGHT}px` });
        } else {
            this._deps.host.setPopupHeight(alert.root, alert.secondaryWrap);
        }
    }
}
